/**
 * RecurringInvoices — factures récurrentes / abonnements.
 *
 * Sprint P1b (13/02/2026) : refonte du formulaire avec :
 *  - `ClientCombobox` (autocomplete sur clients existants + création inline)
 *  - `ProductAutocomplete` par ligne (auto-fill PU HT / TVA depuis catalogue)
 *  - Preview TTC en temps réel (aligné computeTotals de l'éditeur facture)
 *  - Résumé client sélectionné + lien "Voir la fiche client"
 *
 * Alignement UX avec `SalesDocumentEditor.jsx` — un SaaS pro ne demande jamais
 * de retaper une donnée déjà en base.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Repeat, Plus, Play, Pause, Trash2, PlayCircle,
  Loader2, ExternalLink,
} from "lucide-react";
import ClientCombobox from "@/components/ClientCombobox";
import ProductAutocomplete from "@/components/ProductAutocomplete";

const FREQ_LABEL = { monthly: "Mensuel", quarterly: "Trimestriel", yearly: "Annuel" };
const STATUS_STYLE = {
  active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  paused: "bg-amber-50 text-amber-700 border-amber-200",
  ended:  "bg-slate-100 text-slate-500 border-slate-200",
};
const fmtDate = (d) => (!d ? "—" : new Date(d).toLocaleDateString("fr-FR"));
const fmtEuro = (n) => `${(Number(n) || 0).toLocaleString("fr-FR", {
  minimumFractionDigits: 2, maximumFractionDigits: 2,
})} €`;

const emptyClient = () => ({
  name: "", email: "", address: "", postal_code: "",
  city: "", country: "France", siren: "", siret: "", vat_number: "",
});
const emptyTemplate = () => ({
  label: "",
  client_id: null,   // FK vers `clients` (rempli via ClientCombobox)
  client: emptyClient(),
  lines: [{ description: "", quantity: 1, unit_price_ht: 0, vat_rate: 20, discount_percent: 0 }],
  frequency: "monthly",
  start_date: new Date().toISOString().slice(0, 10),
  end_date: "",
  payment_terms_days: 30,
  notes: "",
  auto_issue: true,
});

/** Preview TTC — même formule que SalesDocumentEditor (remise ligne + TVA). */
function previewTotals(lines) {
  let ht = 0, tva = 0;
  for (const l of (lines || [])) {
    const qty = Number(l.quantity) || 0;
    const pu = Number(l.unit_price_ht) || 0;
    const discount = Math.min(100, Math.max(0, Number(l.discount_percent) || 0));
    const vat = Number(l.vat_rate) || 0;
    const line_ht = qty * pu * (1 - discount / 100);
    ht += line_ht;
    tva += line_ht * (vat / 100);
  }
  return { ht: Math.round(ht * 100) / 100,
           tva: Math.round(tva * 100) / 100,
           ttc: Math.round((ht + tva) * 100) / 100 };
}

export default function RecurringInvoices({ embedded = false } = {}) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | template obj
  const [saving, setSaving] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/invoice-templates");
      setTemplates(r.data.templates || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => setEditing(emptyTemplate());
  const openEdit = (t) => setEditing({
    ...t,
    client_id: t.client_id || null,
    end_date: t.end_date || "",
    client: { ...emptyClient(), ...t.client },
    lines: t.lines.map((l) => ({ ...l })),
  });

  const save = async () => {
    if (!editing.label.trim()) { toast.error("Libellé requis"); return; }
    if (!editing.client.name.trim()) { toast.error("Sélectionne un client"); return; }
    if (!editing.lines.length
        || !editing.lines.some((l) => (l.description || "").trim())) {
      toast.error("Au moins une ligne avec une désignation");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        label: editing.label,
        client: editing.client,
        lines: editing.lines
          .filter((l) => (l.description || "").trim())
          .map((l) => ({
            description: l.description, quantity: Number(l.quantity),
            unit_price_ht: Number(l.unit_price_ht),
            vat_rate: Number(l.vat_rate),
            discount_percent: Number(l.discount_percent) || 0,
          })),
        frequency: editing.frequency,
        start_date: editing.start_date,
        end_date: editing.end_date || null,
        payment_terms_days: Number(editing.payment_terms_days) || 30,
        notes: editing.notes || null,
        auto_issue: !!editing.auto_issue,
      };
      if (editing.id) {
        await api.patch(`/invoice-templates/${editing.id}`, payload);
        toast.success("Abonnement mis à jour");
      } else {
        const r = await api.post("/invoice-templates", payload);
        const first = r?.data?.first_invoice;
        if (first?.id) {
          toast.success(
            `Abonnement créé — Facture ${first.number || "brouillon"} émise`,
            {
              action: {
                label: "Voir la facture",
                onClick: () => window.open(`/app/invoices/${first.id}`, "_blank"),
              },
              duration: 8000,
            }
          );
        } else {
          toast.success("Abonnement créé");
        }
      }
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const doAction = async (id, action) => {
    try {
      await api.post(`/invoice-templates/${id}/${action}`);
      toast.success(action === "pause" ? "Abonnement en pause"
        : action === "resume" ? "Abonnement repris"
        : "Facture générée");
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const doDelete = async (id) => {
    try {
      await api.delete(`/invoice-templates/${id}`);
      toast.success("Abonnement supprimé");
      setConfirmDeleteId(null);
      load();
    } catch (e) { toast.error(apiError(e)); }
  };

  // ============ Client picker handlers ============
  const onClientPicked = (c) => setEditing((prev) => ({
    ...prev,
    client_id: c.id,
    client: {
      name: c.name || "", email: c.email || "",
      address: c.address || "", postal_code: c.postal_code || "",
      city: c.city || "", country: c.country || "France",
      siren: c.siren || "", siret: c.siret || "",
      vat_number: c.vat_number || "",
    },
  }));
  const onClientClear = () => setEditing((prev) => ({
    ...prev, client_id: null, client: emptyClient(),
  }));

  // ============ Line handlers ============
  const updLine = (i, patch) => setEditing((prev) => {
    const lines = [...prev.lines];
    lines[i] = { ...lines[i], ...patch };
    return { ...prev, lines };
  });
  const addLine = () => setEditing((prev) => ({
    ...prev, lines: [...prev.lines,
      { description: "", quantity: 1, unit_price_ht: 0, vat_rate: 20, discount_percent: 0 },
    ],
  }));
  const removeLine = (i) => setEditing((prev) => ({
    ...prev, lines: prev.lines.filter((_, idx) => idx !== i),
  }));
  const selectProduct = (i, p) => updLine(i, {
    description: p.name,
    unit_price_ht: Number(p.unit_price_ht) || 0,
    vat_rate: Number(p.vat_rate) || 20,
  });

  const totals = useMemo(
    () => previewTotals(editing?.lines || []),
    [editing?.lines]);

  return (
    <div className="space-y-6" data-testid="recurring-invoices-page">
      <div className="flex items-center justify-between">
        <div>
          {!embedded && (
            <h1 className="text-3xl font-semibold tracking-tight text-slate-900"
                style={{ fontFamily: "Manrope, sans-serif" }}>
              Factures récurrentes
            </h1>
          )}
          <p className="mt-1 text-sm text-slate-500">
            Abonnements et facturation périodique automatique. Le cron s'exécute chaque jour à 5h UTC.
          </p>
        </div>
        <Button onClick={openNew} className="bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="new-recurring-btn">
          <Plus className="w-4 h-4 mr-1.5" /> Nouvel abonnement
        </Button>
      </div>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Repeat className="w-4 h-4 text-blue-600" />
            Modèles actifs ({templates.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
          ) : templates.length === 0 ? (
            <div className="p-16 text-center">
              <Repeat className="w-8 h-8 text-slate-300 mx-auto" />
              <p className="mt-3 text-sm text-slate-500">Aucun abonnement configuré</p>
              <Button variant="outline" onClick={openNew} className="mt-3">
                Créer votre premier abonnement
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-slate-50/80">
                  <TableHead className="text-xs uppercase text-slate-500 font-semibold">Libellé</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 font-semibold">Client</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 font-semibold">Fréquence</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 font-semibold">Prochaine émission</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 font-semibold text-right">Générées</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 font-semibold">Statut</TableHead>
                  <TableHead className="text-xs uppercase text-slate-500 font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((t, idx) => (
                  <TableRow key={t.id} className="cursor-pointer hover:bg-slate-50"
                    data-testid={`recurring-row-${idx}`}
                    onClick={() => openEdit(t)}>
                    <TableCell className="font-medium text-slate-900">{t.label}</TableCell>
                    <TableCell className="text-sm text-slate-600">{t.client?.name}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200 text-[10px] uppercase">
                        {FREQ_LABEL[t.frequency] || t.frequency}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm font-mono text-slate-700">
                      {fmtDate(t.next_run_at)}
                    </TableCell>
                    <TableCell className="text-sm text-right font-mono">{t.generated_count || 0}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`text-[10px] uppercase ${STATUS_STYLE[t.status]}`}>
                        {({active:"Actif",paused:"En pause",ended:"Terminé"}[t.status]) || t.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                      <div className="flex gap-1 justify-end">
                        <Button variant="ghost" size="sm" className="h-8"
                                onClick={() => doAction(t.id, "run-now")}
                                title="Générer maintenant"
                                data-testid={`run-now-${idx}`}>
                          <PlayCircle className="w-4 h-4 text-blue-600" />
                        </Button>
                        {t.status === "active" ? (
                          <Button variant="ghost" size="sm" className="h-8"
                                  onClick={() => doAction(t.id, "pause")}
                                  title="Mettre en pause"
                                  data-testid={`pause-${idx}`}>
                            <Pause className="w-4 h-4 text-amber-600" />
                          </Button>
                        ) : t.status === "paused" ? (
                          <Button variant="ghost" size="sm" className="h-8"
                                  onClick={() => doAction(t.id, "resume")}
                                  title="Reprendre"
                                  data-testid={`resume-${idx}`}>
                            <Play className="w-4 h-4 text-emerald-600" />
                          </Button>
                        ) : null}
                        <Button variant="ghost" size="sm" className="h-8"
                                onClick={() => setConfirmDeleteId(t.id)}
                                title="Supprimer"
                                data-testid={`delete-${idx}`}>
                          <Trash2 className="w-4 h-4 text-red-600" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modal édition */}
      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.id ? "Modifier l'abonnement" : "Nouvel abonnement"}</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">
              Configure la facturation automatique récurrente pour ce client (mensuel / trimestriel / annuel).
            </DialogDescription>
          </DialogHeader>
          {editing && (
            <div className="space-y-5">
              {/* Libellé */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500">
                  Libellé interne
                </Label>
                <Input id="tpl-label" name="tpl-label" value={editing.label}
                        onChange={(e) => setEditing({ ...editing, label: e.target.value })}
                        placeholder="Ex: Abonnement SaaS Pro — Cabinet Meunier"
                        data-testid="tpl-label" />
                <p className="text-[11px] text-slate-400 mt-1">
                  Nom interne pour retrouver ce modèle. Le client ne le voit pas.
                </p>
              </div>

              {/* Client — combobox intelligent */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <Label className="text-xs uppercase tracking-wider text-slate-500">
                    Client destinataire
                  </Label>
                  {editing.client_id && (
                    <Link to={`/app/clients?id=${editing.client_id}`}
                          target="_blank"
                          className="text-[11px] text-blue-600 hover:underline flex items-center gap-1"
                          data-testid="tpl-client-link">
                      Voir la fiche <ExternalLink className="w-3 h-3" />
                    </Link>
                  )}
                </div>
                <ClientCombobox
                  value={editing.client_id
                    ? { id: editing.client_id, name: editing.client.name, siret: editing.client.siret }
                    : null}
                  onSelect={onClientPicked}
                  onClear={onClientClear}
                />
                {editing.client.name && (
                  <div className="mt-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-xs text-slate-700 space-y-0.5"
                        data-testid="tpl-selected-client">
                    {editing.client.email && <div>📧 {editing.client.email}</div>}
                    {(editing.client.address || editing.client.city) && (
                      <div>📍 {editing.client.address}{(editing.client.postal_code || editing.client.city) && `, ${editing.client.postal_code || ""} ${editing.client.city || ""}`}</div>
                    )}
                    {(editing.client.siret || editing.client.siren) && (
                      <div className="font-mono">🏢 {editing.client.siret ? `SIRET ${editing.client.siret}` : `SIREN ${editing.client.siren}`}</div>
                    )}
                    {editing.client.vat_number && (
                      <div className="font-mono">💶 TVA {editing.client.vat_number}</div>
                    )}
                  </div>
                )}
              </div>

              {/* Cadence & délai */}
              <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs uppercase text-slate-500">Fréquence</Label>
                  <Select value={editing.frequency}
                          onValueChange={(v) => setEditing({ ...editing, frequency: v })}>
                    <SelectTrigger data-testid="tpl-frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Mensuel</SelectItem>
                      <SelectItem value="quarterly">Trimestriel</SelectItem>
                      <SelectItem value="yearly">Annuel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Première facturation le</Label>
                  <Input id="tpl-start-date" name="tpl-start-date" type="date" value={editing.start_date}
                          onChange={(e) => setEditing({ ...editing, start_date: e.target.value })}
                          data-testid="tpl-start-date" />
                  <p className="text-[11px] text-slate-400 mt-1">
                    Si aujourd'hui ou passée, la 1ʳᵉ facture sera émise dès l'enregistrement.
                  </p>
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Dernière facturation le (optionnel)</Label>
                  <Input id="tpl-end-date" name="tpl-end-date" type="date" value={editing.end_date || ""}
                          onChange={(e) => setEditing({ ...editing, end_date: e.target.value })}
                          data-testid="tpl-end-date" />
                  <p className="text-[11px] text-slate-400 mt-1">
                    Laisse vide pour un abonnement sans terme.
                  </p>
                </div>
                <div>
                  <Label className="text-xs uppercase text-slate-500">Échéance de paiement (jours)</Label>
                  <Input id="tpl-payment-terms" name="tpl-payment-terms" type="number" min="0" max="180" value={editing.payment_terms_days}
                          onChange={(e) => setEditing({ ...editing, payment_terms_days: e.target.value })}
                          data-testid="tpl-payment-terms" />
                  <p className="text-[11px] text-slate-400 mt-1">
                    Nb de jours après émission pour régler chaque facture (30 par défaut).
                  </p>
                </div>
              </div>

              {/* Lignes avec ProductAutocomplete */}
              <div>
                <Label className="text-xs uppercase tracking-wider text-slate-500 mb-2 block">
                  Lignes récurrentes
                </Label>
                <div className="rounded-md border border-slate-200 overflow-hidden">
                  <div className="grid grid-cols-12 gap-2 items-center bg-slate-50 px-2 py-1.5 text-[10px] uppercase tracking-wider text-slate-500 font-semibold">
                    <div className="col-span-5">Désignation</div>
                    <div className="col-span-2 text-right">Qté</div>
                    <div className="col-span-2 text-right">PU HT (€)</div>
                    <div className="col-span-2 text-right">TVA %</div>
                    <div className="col-span-1"></div>
                  </div>
                  <div className="divide-y divide-slate-100">
                    {editing.lines.map((ln, i) => (
                      <div key={i} className="grid grid-cols-12 gap-2 items-center px-2 py-2"
                           data-testid={`tpl-line-${i}`}>
                        <div className="col-span-5">
                          <ProductAutocomplete
                            id={`tpl-line-desc-${i}`}
                            name={`tpl-line-desc-${i}`}
                            value={ln.description}
                            onChange={(v) => updLine(i, { description: v })}
                            onSelectProduct={(p) => selectProduct(i, p)}
                            placeholder="Tape 2 lettres pour chercher dans le catalogue…"
                            data-testid={`tpl-line-desc-${i}`}
                            className="h-9" />
                        </div>
                        <div className="col-span-2">
                          <Input id={`tpl-line-qty-${i}`} name={`tpl-line-qty-${i}`} type="number" step="0.01" value={ln.quantity}
                                  onChange={(e) => updLine(i, { quantity: e.target.value })}
                                  className="h-9 text-right font-mono"
                                  data-testid={`tpl-line-qty-${i}`} />
                        </div>
                        <div className="col-span-2">
                          <Input id={`tpl-line-pu-${i}`} name={`tpl-line-pu-${i}`} type="number" step="0.01" value={ln.unit_price_ht}
                                  onChange={(e) => updLine(i, { unit_price_ht: e.target.value })}
                                  className="h-9 text-right font-mono"
                                  data-testid={`tpl-line-pu-${i}`} />
                        </div>
                        <div className="col-span-2">
                          <Select value={String(ln.vat_rate)}
                                  onValueChange={(v) => updLine(i, { vat_rate: Number(v) })}>
                            <SelectTrigger className="h-9 font-mono text-sm"
                                            data-testid={`tpl-line-vat-${i}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="20">20 %</SelectItem>
                              <SelectItem value="10">10 %</SelectItem>
                              <SelectItem value="5.5">5,5 %</SelectItem>
                              <SelectItem value="2.1">2,1 %</SelectItem>
                              <SelectItem value="0">0 %</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                        <div className="col-span-1 flex justify-end">
                          {editing.lines.length > 1 && (
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0"
                                    onClick={() => removeLine(i)}
                                    data-testid={`tpl-line-remove-${i}`}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <Button variant="outline" size="sm" onClick={addLine}
                        className="mt-2" data-testid="tpl-add-line">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Ajouter une ligne
                </Button>
              </div>

              {/* Preview TTC live */}
              <div className="rounded-md border border-blue-200 bg-blue-50/50 p-3"
                   data-testid="tpl-preview-totals">
                <div className="grid grid-cols-3 gap-2 text-sm">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">Total HT</div>
                    <div className="font-mono font-semibold text-slate-800">{fmtEuro(totals.ht)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-slate-500">TVA</div>
                    <div className="font-mono text-slate-700">{fmtEuro(totals.tva)}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-blue-700">Total TTC / cycle</div>
                    <div className="font-mono font-bold text-blue-800 text-base">{fmtEuro(totals.ttc)}</div>
                  </div>
                </div>
                <p className="text-[11px] text-slate-500 mt-2 italic">
                  💡 Ce montant sera facturé à chaque échéance ({FREQ_LABEL[editing.frequency]?.toLowerCase()}).
                </p>
              </div>

              <div>
                <Label className="text-xs uppercase text-slate-500">Notes (visibles sur la facture)</Label>
                <Textarea id="tpl-notes" name="tpl-notes" rows={2} value={editing.notes || ""}
                           onChange={(e) => setEditing({ ...editing, notes: e.target.value })}
                           data-testid="tpl-notes" />
              </div>

              <label className="flex items-center gap-2 text-sm">
                <input id="tpl-auto-issue" name="tpl-auto-issue"
                        type="checkbox" checked={!!editing.auto_issue}
                        onChange={(e) => setEditing({ ...editing, auto_issue: e.target.checked })}
                        data-testid="tpl-auto-issue" />
                <span>Émettre automatiquement (attribuer numéro + envoyer PDF au client). Sinon, la facture reste en brouillon.</span>
              </label>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)} disabled={saving}>Annuler</Button>
            <Button onClick={save} disabled={saving}
                     className="bg-blue-600 hover:bg-blue-700 text-white"
                     data-testid="save-tpl-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : null}
              {editing?.id ? "Enregistrer" : "Créer l'abonnement"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Confirm delete */}
      <AlertDialog open={!!confirmDeleteId} onOpenChange={(v) => !v && setConfirmDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Supprimer cet abonnement ?</AlertDialogTitle>
            <AlertDialogDescription>
              Les factures déjà générées seront conservées. Aucune nouvelle facture ne sera émise après suppression.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction className="bg-red-600 hover:bg-red-700 text-white"
                                onClick={() => doDelete(confirmDeleteId)}>
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
